import React from "react";
import AsyncState from "react-async-stateful";

import * as Utils from "./Utils";
import * as NavigationUtils from "./NavigationUtils";
import { Falsy } from "../types/UtilTypes";
import {
	Align,
	BorderColor,
	FlexAlign,
	Size,
	Variant,
} from "../types/MiscTypes";

export const VisibleContext = React.createContext(true);
export const ViewContext = React.createContext<() => Element>(
	() => document.body,
);

/**
 * @deprecated
 */
const updateState = (component: any, state: any) => {
	return new Promise((resolve, reject) => {
		try {
			component.setState(state, resolve);
		} catch (error) {
			reject(error);
		}
	});
};

/**
 * Takes an object of {[state key] : Promise} and updates async state with the promises
 *
 * @deprecated
 *
 * @param {React.Component} component
 * @param asyncUpdate
 * @param callback
 * @returns {Promise<Readonly<S>>}
 */
export const old__setStateAsync = async (
	component: any,
	asyncUpdate: any,
	callback: any,
) => {
	if (!asyncUpdate || typeof asyncUpdate !== "object") {
		throw new Error("Please provide an object as the async update");
	}

	const promises = [];

	/**
	 * What is the lock map?
	 * ---
	 *
	 * The lock map is a mechanism that only allows the lastest call of setStateAsync of a key to update state.
	 * For example, on the user profile page the userId can change and all of the async states are re-requested.
	 * Only the lastest call with trigger state updates
	 */
	let lockMap = component.__asyncStateLocks;
	if (!lockMap) {
		lockMap = component.__asyncStateLocks = {};
	}

	for (const key in asyncUpdate) {
		const lock = (lockMap[key] = Date.now() + Math.random());

		promises.push(
			new Promise<void>(async (resolve) => {
				let fufilled = false;

				let options = {};
				let promise = asyncUpdate[key];
				if (Array.isArray(promise)) {
					options = promise[1] || {};
					promise = promise[0];
				}

				const timeout = setTimeout(() => {
					// if outdated call or fufilled already, don't set pending state
					if (lockMap[key] !== lock) {
						return resolve();
					}
					if (!fufilled) {
						const currentAsyncState = component.state[key];
						const updated = (options as any).refresh
							? AsyncState.refresh(currentAsyncState)
							: AsyncState.submit(currentAsyncState);
						updateState(component, { [key]: updated });
					}
				}, 200);

				try {
					if (typeof promise === "function") {
						promise = promise();
					}
					const res = await promise;
					// if we are outdated don't do anything
					if (lockMap[key] !== lock) {
						return resolve();
					}
					fufilled = true;
					const value = typeof res === "object" ? res.data || res : res;
					await updateState(component, (state: any) => ({
						[key]: AsyncState.resolve(state[key], value),
					}));
				} catch (error) {
					// if we are outdated don't do anything
					if (lockMap[key] !== lock) {
						return resolve();
					}
					fufilled = true;
					const name =
						component.displayName ||
						component.name ||
						component.constructor.name ||
						"Component";
					console.warn(`Error setting ${name}.state.${key}:`);
					console.warn(error);
					await updateState(component, (state: any) => ({
						[key]: AsyncState.reject(state[key], error),
					}));
				}
				clearTimeout(timeout);
				resolve();
			}),
		);
	}
	await Promise.all(promises);
	if (callback) {
		// eslint-disable-next-line
		callback(component.state);
	}

	return component.state;
};

/**
 * @deprecated - should be replaced with hook
 */
export const old__bindQueryParam = (
	component: any,
	key: any,
	optionsOrCallback: any = {},
) => {
	let { callback, map } = optionsOrCallback || {};
	if (typeof optionsOrCallback === "function") {
		callback = optionsOrCallback;
	}

	let initial = NavigationUtils.getQueryParam(key);
	if (map) {
		initial = map(initial);
	}
	component.setState(
		{ [key]: initial },
		callback && (() => callback.call(component, initial)),
	);

	const listener = NavigationUtils.onHistoryChange(() => {
		let param = NavigationUtils.getQueryParam(key);
		if (map) {
			param = map(param);
		}
		if (param !== component.state[key]) {
			component.setState(
				{ [key]: param },
				callback && (() => callback.call(component, param)),
			);
		}
	});

	Utils.overrideProperty(component, "componentWillUnmount", (original) => {
		return () => {
			NavigationUtils.clearHistoryChange(listener);
			return original && original();
		};
	});
};

let portals: Element | null = null;

export const getPortalElement = (name: string) => {
	const element = document.querySelector(`.Portal[name=${name}]`);
	if (element) return element;
	portals = portals || document.querySelector(".Portals");
	if (!portals) {
		portals = document.createElement("div");
		portals.setAttribute("class", "Portals");
		document.body.appendChild(portals);
	}
	const portal = document.createElement("div");
	portal.setAttribute("class", "Portal");
	portal.setAttribute("name", name);
	portals.appendChild(portal);
	return portal;
};

let rootElement: (Element & { inert?: boolean }) | null = null;

export const getRootElement = (): Element & { inert?: boolean } => {
	if (rootElement) return rootElement;
	return (rootElement = document.querySelector("#root"))!;
};

export const updateRefValue = <T>(ref: React.Ref<T>, value: T) => {
	if (!ref) return;
	if (typeof ref === "function") {
		ref(value);
	} else if (typeof ref === "object") {
		Object.defineProperty(ref, "current", { value });
	}
};

export const multiRef = <T>(
	...refs: Array<React.Ref<T> | Falsy>
): React.Ref<T> => {
	return (value: T) => {
		for (const ref of refs) {
			if (ref) {
				updateRefValue(ref, value);
			}
		}
	};
};

export interface LayoutProps {
	position?: "relative" | "absolute" | "fixed";
	padding?: Size | null | "none";
	paddingTop?: Size | null | "none";
	paddingLeft?: Size | null | "none";
	paddingRight?: Size | null | "none";
	paddingBottom?: Size | null | "none";
	paddingVertical?: Size | null | "none";
	paddingHorizontal?: Size | null | "none";
	margin?: Size | null | "none";
	marginTop?: Size | null | "none";
	marginLeft?: Size | null | "none";
	marginRight?: Size | null | "none";
	marginBottom?: Size | null | "none";
	marginHorizontal?: Size | null | "none";
	marginVertical?: Size | null | "none";
	flexDirection?: "column" | "row";
	alignContent?: FlexAlign;
	alignItems?: FlexAlign;
	justifyContent?: FlexAlign;
	rounded?: boolean;
	roundedLeft?: boolean;
	roundedRight?: boolean;
	roundedTop?: boolean;
	roundedBottom?: boolean;
	border?: BorderColor;
	borderTop?: BorderColor;
	borderLeft?: BorderColor;
	borderBottom?: BorderColor;
	borderRight?: BorderColor;
	borderHorizontal?: BorderColor;
	borderVertical?: BorderColor;
	width?: "full";
	textAlign?: Align;
	textSize?: Size;
	color?: Variant | "main" | Falsy;
}

export const computeLayoutClasses = (
	props: LayoutProps,
): [Array<string | Falsy>, Object] => {
	const {
		flexDirection,
		padding,
		paddingBottom,
		paddingHorizontal,
		paddingLeft,
		paddingRight,
		paddingTop,
		paddingVertical,
		margin,
		marginBottom,
		marginHorizontal,
		marginLeft,
		marginRight,
		marginTop,
		marginVertical,
		alignContent,
		alignItems,
		justifyContent,
		rounded,
		roundedBottom,
		roundedLeft,
		roundedRight,
		roundedTop,
		border,
		borderRight,
		borderHorizontal,
		borderLeft,
		borderTop,
		borderVertical,
		borderBottom,
		width,
		textAlign,
		position,
		textSize,
		color,
		...remaining
	} = props;

	let flexClassName = undefined;
	if (flexDirection === "column") {
		flexClassName = "--column";
	} else if (flexDirection === "row") {
		flexClassName = "--row";
	}

	return [
		[
			position && `--position-${position}`,
			width && `--width-${width}`,
			paddingTop !== undefined && `--pt-${paddingTop || "none"}`,
			paddingBottom !== undefined && `--pb-${paddingBottom || "none"}`,
			paddingLeft !== undefined && `--pl-${paddingLeft || "none"}`,
			paddingRight !== undefined && `--pr-${paddingRight || "none"}`,
			paddingHorizontal !== undefined && `--ph-${paddingHorizontal || "none"}`,
			paddingVertical !== undefined && `--pv-${paddingVertical || "none"}`,
			padding !== undefined && `--p-${padding || "none"}`,
			margin !== undefined && `--m-${margin || "none"}`,
			marginTop !== undefined && `--mt-${marginTop || "none"}`,
			marginBottom !== undefined && `--mb-${marginBottom || "none"}`,
			marginLeft !== undefined && `--ml-${marginLeft || "none"}`,
			marginRight !== undefined && `--mr-${marginRight || "none"}`,
			marginHorizontal !== undefined && `--mh-${marginHorizontal || "none"}`,
			marginVertical !== undefined && `--mv-${marginVertical || "none"}`,
			alignContent && `--align-content-${alignContent}`,
			alignItems && `--align-items-${alignItems}`,
			justifyContent && `--justify-content-${justifyContent}`,
			rounded !== undefined && `--rounded${rounded ? "" : "-none"}`,
			roundedTop !== undefined && `--rounded-top${roundedTop ? "" : "-none"}`,
			roundedBottom !== undefined &&
				`--rounded-bottom${roundedBottom ? "" : "-none"}`,
			roundedLeft !== undefined &&
				`--rounded-left${roundedLeft ? "" : "-none"}`,
			roundedRight !== undefined &&
				`--rounded-right${roundedRight ? "" : "-none"}`,
			border && `--border-${border}`,
			borderTop && `--border-top-${borderTop}`,
			borderLeft && `--border-left-${borderLeft}`,
			borderRight && `--border-right-${borderRight}`,
			borderBottom && `--border-bottom-${borderBottom}`,
			borderVertical && `--border-vertical-${borderVertical}`,
			borderHorizontal && `--border-horizontal-${borderHorizontal}`,
			flexClassName,
			textAlign && `--text-align-${textAlign}`,
			textSize && `--text-size-${textSize}`,
			color && `--color-${color}`,
		],
		remaining,
	];
};
